import React from 'react';
import { motion } from 'framer-motion';

/**
 * Shared recipe/option card — a grey icon tile + title + description,
 * always visible, with a soft border/background lift on hover. Used by both the
 * level-1 recipe picker (RecipeSelector) and the level-2 create options on a
 * generation node (Variation / Edit image / Design / Video) so both tiers share
 * one consistent look. `icon` is an already-rendered node; the tile drives its
 * colour via currentColor, so stroke=currentColor icons just work.
 */
export const RecipeOptionCard: React.FC<{
    icon: React.ReactNode;
    title: string;
    description: string;
    index?: number;
    onClick: () => void;
}> = ({ icon, title, description, index = 0, onClick }) => (
    <motion.div
        initial={{ opacity: 0, x: -20, scale: 0.9 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 400, damping: 28, delay: index * 0.04 }}
    >
        <div
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            // Gentle grey contour on hover, never a harsh black outline.
            className="group flex cursor-pointer items-center gap-3 rounded-xl border bg-card p-3 transition-colors duration-300 hover:border-input hover:bg-muted"
        >
            {/* Icon — grey tile with a soft contour. */}
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border bg-muted text-foreground transition-colors duration-300 group-hover:border-input">
                {icon}
            </div>

            {/* Text — title + description, always visible. */}
            <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">
                    {title}
                </div>
                <div className="mt-0.5 text-xs leading-[1.45] text-muted-foreground">
                    {description}
                </div>
            </div>
        </div>
    </motion.div>
);
